import { useEffect, useMemo, useState } from 'react';

import { getSingleServiceProvider } from '@/store/service-providers/service-providers.api';
import type { SingleServiceProvider } from '@/store/service-providers/service-providers.dto';

export type ServiceProvidersMap = Map<string, SingleServiceProvider>;

export const useServiceProvidersMap = (
  ids: string[],
): { map: ServiceProvidersMap; loading: boolean } => {
  const [serviceProviders, setServiceProviders] = useState<SingleServiceProvider[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ids.length === 0) {
      setServiceProviders([]);
      return;
    }

    const fetchServiceProviders = async () => {
      setLoading(true);
      try {
        const results = await Promise.all(
          ids.map((id) => getSingleServiceProvider(id).catch(() => null)),
        );
        setServiceProviders(results.filter((sp): sp is SingleServiceProvider => sp !== null));
      } finally {
        setLoading(false);
      }
    };

    void fetchServiceProviders();
  }, [ids.join(',')]);

  const map = useMemo(() => {
    const m = new Map<string, SingleServiceProvider>();
    for (const sp of serviceProviders) {
      m.set(sp.id, sp);
    }
    return m;
  }, [serviceProviders]);

  return { map, loading };
};
